import { Formik } from 'formik';
import { useContext, useState, useEffect } from 'react';

import { CustomerEditContext } from './CustomerEdit';

import { CloseButton } from '@components/ActionButtons';
import { Box } from '@components/Box';
import {
  FormSectionContainer,
  FormButtonsContainer,
} from '@components/Container';
import {
  FormikInput,
  CancelButton,
  SubmitButton,
  FormikCheckbox,
} from '@components/Form';
import { ActionableSelect } from '@components/Form/ActionableSelect';
import { OkButton } from '@components/Form/OkButton';
import { Loading } from '@components/Loading';
import { Modal } from '@components/Modal';
import { FormikSelectOption } from '@models/common/FormikSelectOption';
import {
  ShippingAddressViewModel,
  shippingAddressDefaultValue,
  shippingAddressValidation,
} from '@models/ShippingAddress';
import { CustomerService } from '@services/Customer';
import { ShippingAddressService } from '@services/ShippingAddress';

export const CustomerEditShippingAddressSelect = () => {
  const { customer, setCustomer } = useContext(CustomerEditContext);
  const [isCustomerShippingsLoading, setIsCustomerShippingsLoading] =
    useState(true);
  const customerShippingAddresses = customer.shippingAddresses || [];
  const [
    customerShippingAddressSelectOptions,
    setCustomerShippingAddressSelectOptions,
  ] = useState<FormikSelectOption[]>([]);
  const [isShowAddShippingAddress, setIsShowAddShippingAddress] =
    useState(false);
  const [isShowEditShippingAddress, setIsShowEditShippingAddress] =
    useState(false);
  const [isShowDeleteShippingAddress, setIsShowDeleteShippingAddress] =
    useState(false);
  const [shippingAddressToEdit, setShippingAddressToEdit] =
    useState<ShippingAddressViewModel | null>();
  const [isLoading, setIsLoading] = useState(true);

  const loadShippingAddresses = () => {
    CustomerService.getCustomerShippingAddresses(Number(customer.id)).then(
      (result) => {
        if (result.isSuccess && 'data' in result) {
          setCustomer((prevState) => ({
            ...prevState,
            shippingAddresses: result.data,
          }));
        }

        setIsCustomerShippingsLoading(false);
      }
    );
  };

  const loadShippingAddressOptions = () => {
    const customerShippingAddressSelection = [{ value: '0', text: '' }].concat(
      customerShippingAddresses.map((row) => ({
        value: `${row.id}`,
        text: `${row.address1} ${row.address2}, ${row.city}, ${row.state} ${row.postalCode}, ${row.country}`,
      }))
    );
    setCustomerShippingAddressSelectOptions(customerShippingAddressSelection);
  };

  useEffect(() => {
    loadShippingAddressOptions();
  }, [customer.shippingAddresses]);

  useEffect(() => {
    if (!isCustomerShippingsLoading) {
      setIsLoading(false);
    }
  }, [isCustomerShippingsLoading]);

  useEffect(() => {
    loadShippingAddresses();
  }, []);

  if (isLoading) {
    return <Loading />;
  } else {
    return (
      <>
        <ActionableSelect
          label="Shipping Address"
          value={`${customer.shippingID}`}
          selection={customerShippingAddressSelectOptions}
          onAddAction={() => {
            setIsShowAddShippingAddress(true);
          }}
          onEditAction={() => {
            if (customer.shippingID == undefined || customer.shippingID == 0)
              return;

            const selectedShippingAddress = customerShippingAddresses.find(
              (row) => row.id == customer.shippingID
            );

            if (selectedShippingAddress == undefined) return;

            setShippingAddressToEdit(selectedShippingAddress);
            setIsShowEditShippingAddress(true);
          }}
          onDeleteAction={() => setIsShowDeleteShippingAddress(true)}
          onDuplicateAction={() => {
            const selectedShippingAddress = customerShippingAddresses.find(
              (row) => row.id == customer.shippingID
            );

            if (selectedShippingAddress == undefined) return;

            selectedShippingAddress.id = 0;
            ShippingAddressService.create(selectedShippingAddress).then(
              (result) => {
                if (result.isSuccess && 'data' in result) {
                  const newShippingAddress = {
                    ...selectedShippingAddress,
                    id: result.data.id,
                  };

                  const updatedShippingAddresses =
                    customerShippingAddresses.concat(newShippingAddress);

                  setCustomer({
                    ...customer,
                    shippingAddresses: updatedShippingAddresses,
                  });

                  setShippingAddressToEdit(newShippingAddress);
                  setIsCustomerShippingsLoading(true);
                }
              }
            );
          }}
          onChange={(e) => {
            const updateCustomer = {
              ...customer,
              shippingID: Number(e.target.value),
            };
            setCustomer(updateCustomer);
          }}
        />

        {isShowDeleteShippingAddress && (
          <>
            <Modal show={true}>
              <CloseButton
                onClick={() => setIsShowDeleteShippingAddress(false)}
              />
              <Box size="medium">
                <>
                  <div className="px-4 pb-2">
                    <span>
                      Are you sure you want to delete this record from your
                      Shipping Address lists?
                    </span>
                  </div>
                  <FormButtonsContainer>
                    <div className="space-x-2">
                      <CancelButton
                        onClick={() => setIsShowDeleteShippingAddress(false)}
                      />
                      <OkButton
                        onClick={async () => {
                          const deleteShippingAddress =
                            customerShippingAddresses.find(
                              (row) => row.id == customer.shippingID
                            );

                          if (deleteShippingAddress == undefined) return;

                          const result = await ShippingAddressService.delete(
                            deleteShippingAddress.id
                          );
                          if (result.isSuccess) {
                            const updatedShippingAddresses =
                              customerShippingAddresses.filter(
                                (row) => row.id !== customer.shippingID
                              );

                            const nextShippingId =
                              updatedShippingAddresses.length > 0
                                ? updatedShippingAddresses[0].id
                                : 0;

                            setCustomer({
                              ...customer,
                              shippingID: nextShippingId,
                              shippingAddresses: updatedShippingAddresses,
                            });
                            setIsShowDeleteShippingAddress(false);
                          } else {
                            console.error(
                              'Error: Failed to delete the record.',
                              result.errorMessages
                            );
                            alert(`Error: ${result.errorMessages}`);
                          }
                        }}
                      >
                        Delete
                      </OkButton>
                    </div>
                  </FormButtonsContainer>
                </>
              </Box>
            </Modal>
          </>
        )}

        {isShowAddShippingAddress && (
          <>
            <Modal show={true}>
              <CloseButton onClick={() => setIsShowAddShippingAddress(false)} />

              <Formik
                initialValues={shippingAddressDefaultValue}
                validationSchema={shippingAddressValidation}
                validateOnBlur={true}
                validateOnChange={true}
                onSubmit={(values, actions) => {
                  values.customerId = Number(customer.id);

                  ShippingAddressService.create(values).then((result) => {
                    if (result.isSuccess && 'data' in result) {
                      values.id = result.data.id;
                      const updatedShippingAddresses =
                        customerShippingAddresses.concat(values);

                      let activeId = values.id;

                      if (!values.isActive) {
                        const firstActive = updatedShippingAddresses.find(
                          (item) => item.isActive
                        );
                        activeId = firstActive ? firstActive.id : 0;
                      }

                      setCustomer({
                        ...customer,
                        shippingID: activeId,
                        shippingAddresses: updatedShippingAddresses,
                      });

                      actions.resetForm();
                      setIsShowAddShippingAddress(false);
                    }
                  });
                }}
              >
                {(formikProps) => {
                  return (
                    <>
                      <form method="POST" onSubmit={formikProps.handleSubmit}>
                        <FormSectionContainer>
                          <FormikInput
                            label="Currency Code"
                            name="currencyCode"
                          />
                          <FormikInput label="Address 1" name="address1" />
                          <FormikInput label="Address 2" name="address2" />
                          <FormikInput label="City" name="city" />
                          <FormikInput label="State / Province" name="state" />
                          <FormikInput label="Country" name="country" />
                          <FormikInput label="Postal Code" name="postalCode" />
                          <FormikCheckbox label="Is Active" name="isActive" />
                        </FormSectionContainer>
                        <FormButtonsContainer>
                          <CancelButton
                            onClick={() => {
                              setIsShowAddShippingAddress(false);
                              formikProps.resetForm();
                            }}
                          />

                          <SubmitButton
                            label="Save"
                            disabled={
                              formikProps.isSubmitting || !formikProps.isValid
                            }
                          />
                        </FormButtonsContainer>
                      </form>
                    </>
                  );
                }}
              </Formik>
            </Modal>
          </>
        )}

        {isShowEditShippingAddress && shippingAddressToEdit && (
          <>
            <Modal show={true}>
              <CloseButton
                onClick={() => setIsShowEditShippingAddress(false)}
              />

              <Formik
                initialValues={shippingAddressToEdit}
                validationSchema={shippingAddressValidation}
                validateOnBlur={true}
                validateOnChange={true}
                onSubmit={(values, actions) => {
                  ShippingAddressService.update(Number(values.id), values).then(
                    (result) => {
                      if (result.isSuccess) {
                        const updatedShippingAddresses =
                          customerShippingAddresses.map((row) =>
                            row.id === values.id ? values : row
                          );

                        let activeId = values.id;

                        if (!values.isActive) {
                          const firstActive = updatedShippingAddresses.find(
                            (item) => item.isActive
                          );
                          activeId = firstActive ? firstActive.id : 0;
                        }

                        setCustomer({
                          ...customer,
                          shippingID: activeId,
                          shippingAddresses: updatedShippingAddresses,
                        });

                        actions.resetForm();
                        setIsShowEditShippingAddress(false);
                      }
                    }
                  );
                }}
              >
                {(formikProps) => {
                  return (
                    <>
                      <form method="POST" onSubmit={formikProps.handleSubmit}>
                        <FormSectionContainer>
                          <FormikInput
                            label="Currency Code"
                            name="currencyCode"
                          />
                          <FormikInput label="Address 1" name="address1" />
                          <FormikInput label="Address 2" name="address2" />
                          <FormikInput label="City" name="city" />
                          <FormikInput label="State / Province" name="state" />
                          <FormikInput label="Country" name="country" />
                          <FormikInput label="Postal Code" name="postalCode" />
                          <FormikCheckbox label="Is Active" name="isActive" />
                        </FormSectionContainer>
                        <FormButtonsContainer>
                          <CancelButton
                            onClick={() => {
                              setIsShowEditShippingAddress(false);
                              formikProps.resetForm();
                            }}
                          />

                          <SubmitButton
                            label="Save"
                            disabled={
                              formikProps.isSubmitting || !formikProps.isValid
                            }
                          />
                        </FormButtonsContainer>
                      </form>
                    </>
                  );
                }}
              </Formik>
            </Modal>
          </>
        )}
      </>
    );
  }
};
